// Un seul flux d'événements (SSE) par onglet — proposition UI validée le 07/09/2026.
//
// Avant : la cloche et le bandeau d'appels entrants ouvraient chacun leur
// EventSource sur /api/events/stream, et la cloche en rouvrait un à chaque
// changement de ses dépendances (ouverture du menu) : deux à trois connexions
// longues par onglet, autant côté serveur.
//
// Maintenant : une connexion partagée, comptée par abonné. `openSharedEventStream()`
// rend un objet au contrat d'EventSource (onmessage / onerror / close) pour que
// les consommateurs existants changent une ligne ; `useEventStream(handler)`
// est la forme hook pour les prochains.
import { useEffect, useRef } from 'react';

const STREAM_URL = '/api/events/stream';
let source = null;          // l'unique EventSource réel
const subscribers = new Set(); // { onmessage, onerror }

function ensureSource() {
  if (source || typeof window === 'undefined' || typeof window.EventSource === 'undefined') return;
  source = new window.EventSource(STREAM_URL, { withCredentials: true });
  source.onmessage = (ev) => {
    for (const s of Array.from(subscribers)) {
      try { if (typeof s.onmessage === 'function') s.onmessage(ev); } catch (_) { /* un abonné ne casse pas les autres */ }
    }
  };
  source.onerror = (ev) => {
    // EventSource reconnecte de lui-même ; on prévient les abonnés qui veulent le savoir.
    for (const s of Array.from(subscribers)) {
      try { if (typeof s.onerror === 'function') s.onerror(ev); } catch (_) { /* idem */ }
    }
  };
}

function releaseIfIdle() {
  if (subscribers.size === 0 && source) {
    try { source.close(); } catch (_) { /* déjà fermé */ }
    source = null;
  }
}

/** Contrat EventSource minimal (onmessage / onerror / close) sur le flux partagé. */
export function openSharedEventStream() {
  const sub = { onmessage: null, onerror: null };
  subscribers.add(sub);
  ensureSource();
  return {
    get onmessage() { return sub.onmessage; },
    set onmessage(fn) { sub.onmessage = fn; },
    get onerror() { return sub.onerror; },
    set onerror(fn) { sub.onerror = fn; },
    get readyState() { return source ? source.readyState : 2; },
    close() {
      subscribers.delete(sub);
      releaseIfIdle();
    },
  };
}

/** Hook : `handler(data)` reçoit chaque message JSON du flux ; le dernier handler gagne, sans reconnexion. */
export function useEventStream(handler) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    const es = openSharedEventStream();
    es.onmessage = (ev) => {
      let data;
      try { data = JSON.parse(ev.data); } catch (_) { return; }
      ref.current?.(data);
    };
    return () => es.close();
  }, []);
}

/** Pour les tests et le diagnostic : combien d'abonnés partagent le flux. */
export function _eventStreamStats() {
  return { subscribers: subscribers.size, connected: !!source };
}
